<template>
    <form
        :id="id"
        data-form="vee-form"
        @submit.prevent="onSubmitForm"
        @reset.prevent="onResetForm"
        :class="horizontal ? 'vee-form horizontal' : 'vee-form'">
        <slot />
    </form>
</template>

<script lang="ts">
    import {
        PropType,
        defineComponent,
        watch,
        provide,
        reactive,
        toRefs,
        onBeforeUnmount,
        onMounted,
        ref,
    } from 'vue';
    import { FormContext, useForm } from 'vee-validate';
    import { FormErrors, FormValues } from './Form.interfaces';
    import { FieldValidateOn, ValidationSchema } from './Validations/Validations.interfaces';
    import { registerForm, unregisterForm } from '@/helpers/formRegistry';
    import isEqual from 'lodash/isEqual';

    export default defineComponent({
        name: 'FormV2',
        props: {
            id: { type: String, required: true },
            initialValues: { type: Object as PropType<FormValues> },
            validationSchema: { type: Object as PropType<ValidationSchema> },
            validateOn: {
                type: Object as PropType<FieldValidateOn>,
                default: () => ({
                    validateOnMount: false,
                    validateOnInput: false,
                    validateOnChange: true,
                    validateOnBlur: true,
                    validateOnModelUpdate: true,
                }),
            },
            onSubmit: {
                type: Function as PropType<(values: FormValues) => void>,
            },
            onInvalidSubmit: {
                type: Function as PropType<(values: FormValues) => void>,
            },
            onChange: {
                type: Function as PropType<(context: FormContext) => void>,
            },
            onValidChange: {
                type: Function as PropType<(valid: boolean) => void>,
            },
            onErrorChange: {
                type: Function as PropType<(errors: FormErrors) => void>,
            },
            onUnmount: {
                type: Function as PropType<(context: FormContext) => void>,
            },
            autoSubmit: {
                type: Boolean,
            },
            horizontal: {
                type: Boolean,
                default: false,
            },
        },
        setup(props) {
            const reactiveValidateOn = reactive(props.validateOn);
            const prevValues = ref(); // TODO: do we need this.. maybe just stringify
            provide('fieldValidateOn', reactiveValidateOn);

            const form = useForm({
                initialValues: props.initialValues,
                validationSchema: props.validationSchema,
            });

            onMounted(() => {
                registerForm(props.id, () => form.submitForm());
            });

            onBeforeUnmount(() => {
                // Unregister the form from the registry
                unregisterForm(props.id);
                // Trigger the onUnmount callback if provided
                props.onUnmount && props.onUnmount(form);
            });

            watch(form.values, (newValues) => {
                prevValues.value = JSON.stringify(newValues);
                props.onChange && props.onChange(form);
            });

            watch(
                () => form.meta.value.valid,
                (valid) => {
                    if (props.onValidChange) {
                        props.onValidChange(valid);
                    }
                },
                { immediate: true }
            );

            watch(
                () => props.validateOn,
                (newValue, prevValue) => {
                    if (newValue !== prevValue) {
                        Object.assign(reactiveValidateOn, newValue);
                    }
                }
            );

            watch(
                [() => form.meta.value.valid, prevValues],
                (newValues, prevValues) => {
                    const [isValid, values] = newValues;
                    const [oldIsValid, oldValues] = prevValues;

                    if (
                        isValid &&
                        props.autoSubmit &&
                        (!isEqual(values, oldValues) || !isEqual(isValid, oldIsValid))
                    ) {
                        onSubmitForm();
                    }
                },
                { immediate: true }
            );

            watch(
                () => form.errors.value,
                (errors, oldErrors) => {
                    if (!isEqual(errors, oldErrors) && props.onErrorChange) {
                        props.onErrorChange(errors);
                    }
                },
                { immediate: true }
            );

            function onInvalidSubmit(values: FormValues) {
                if (props.onInvalidSubmit && form.meta.value.touched) {
                    props.onInvalidSubmit(values);
                }
            }

            function onSubmitForm() {
                // Use handleSubmit from vee-validate to trigger submission
                form.handleSubmit((values: FormValues) => {
                    props.onSubmit && props.onSubmit(values);
                }, onInvalidSubmit)();
            }

            function onResetForm() {
                form.resetForm({ values: props.initialValues });
            }

            return {
                ...toRefs(reactiveValidateOn),
                onSubmitForm,
                onResetForm,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward './Form.scss';
</style>
